import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  List,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  TimePicker,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useSelector } from "react-redux";
import { baseUrl } from "../../../utils/envAccess";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;

const TIMEZONE_OPTIONS = [
  { label: "Pacific Time", value: "Canada/Pacific" },
  { label: "Mountain Time", value: "Canada/Mountain" },
  { label: "Central Time", value: "Canada/Central" },
  { label: "Eastern Time", value: "Canada/Eastern" },
  { label: "Atlantic Time", value: "Canada/Atlantic" },
];

const LOCATION_OPTIONS = [
  { label: "Calgary", value: "Calgary" },
  { label: "Edmonton", value: "Edmonton" },
];


const FALLBACK_TYPE_OPTIONS = [
  { label: "Virtual", value: 2 },
  { label: "In Person", value: 1 },
];

function buildOffsetIso(date, time, tz) {
  const dt = dayjs.tz(
    `${date.format("YYYY-MM-DD")} ${time.format("HH:mm")}`,
    "YYYY-MM-DD HH:mm",
    tz,
  );
  return dt.format("YYYY-MM-DDTHH:mm:ssZ");
}

function formatIsoForDisplay(iso) {
  const parsed = dayjs(iso);
  if (!parsed.isValid()) return iso;
  return parsed.format("ddd, MMM D • HH:mm (Z)");
}

export default function ZoomAvailability() {
  const [form] = Form.useForm();
  const userAuth = useSelector((s) => s?.authentication?.userAuth);
  const token = userAuth?.obj?.token;

  const defaultName = useMemo(() => {
    const first = userAuth?.obj?.firstName || userAuth?.obj?.firstname;
    const last = userAuth?.obj?.lastName || userAuth?.obj?.lastname;
    return [first, last].filter(Boolean).join(" ").trim();
  }, [userAuth]);

  const [segments, setSegments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [typeOptions, setTypeOptions] = useState(FALLBACK_TYPE_OPTIONS);
  const avilibilityType = Form.useWatch("avilibilityType", form);

  
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${baseUrl}Doctor/ListofAvilibilityType`, {
        headers: { accept: "text/plain", Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        const options = list
          .map((t) => ({
            value: t?.id ?? t?.Id,
            label: t?.name ?? t?.Name ?? "",
          }))
          .filter((o) => o.value != null && o.label);
        if (!cancelled && options.length) setTypeOptions(options);
      })
      .catch(() => {
        // Keep the fallback list - the form still works.
      });
    return () => {
      cancelled = true;
    };
  }, [token]);


  const onAddSegment = async () => {
    try {
      const v = await form.validateFields(["date", "startTime", "endTime", "time_zone"]);
      const { date, startTime, endTime, time_zone } = v;

      if (endTime.isSame(startTime) || endTime.isBefore(startTime)) {
        message.error("End time must be after start time.");
        return;
      }

      const startIso = buildOffsetIso(date, startTime, time_zone);
      const endIso = buildOffsetIso(date, endTime, time_zone);

      
      const newStart = dayjs(startIso);
      const newEnd = dayjs(endIso);
      const conflict = segments.find(
        (s) => newStart.isBefore(dayjs(s.end)) && dayjs(s.start).isBefore(newEnd),
      );
      if (conflict) {
        message.error(
          `This overlaps with ${formatIsoForDisplay(conflict.start)} → ${formatIsoForDisplay(conflict.end)}.`,
        );
        return;
      }

      setSegments((prev) => {
        const next = [...prev, { start: startIso, end: endIso }];
        // de-dupe
        return next.filter((s, idx) => idx === next.findIndex((x) => x.start === s.start && x.end === s.end));
      });
    } catch {
      // validation handled by antd
    }
  };

  const onSubmit = async () => {
    const v = await form.validateFields(["name", "time_zone", "avilibilityType", "location", "default"]);
    if (!token) {
      message.error("You must be signed in.");
      return;
    }
    if (!segments.length) {
      message.error("Add at least one time segment.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: v.name,
        time_zone: v.time_zone,
        location: v.location,
        avilibilityType: v.avilibilityType,
        default: Boolean(v.default),
        segments,
      };

      await axios.post(`${baseUrl}Doctor/CreateZoomAvailability`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          accept: "text/plain",
        },
      });

      message.success("Availability saved.");
      setSegments([]);
      form.setFieldsValue({ startTime: null, endTime: null });
    } catch (e) {
      const errors = e?.response?.data?.Errors || e?.response?.data?.errors;
      const msg =
        (Array.isArray(errors) && errors.length ? errors.join(" ") : null) ||
        e?.response?.data?.message ||
        e?.response?.data?.title ||
        (typeof e?.response?.data === "string" ? e.response.data : null) ||
        "Failed to save availability.";
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: 16 }}>
      <Title level={3} style={{ marginBottom: 6 }}>
        Zoom Availability
      </Title>
      <Text type="secondary">
        Add 30-minute segments (or any duration) for patients to book.
      </Text>

      <Card style={{ marginTop: 16 }} bordered bodyStyle={{ padding: 20 }}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            name: defaultName || "",
            time_zone: "Canada/Mountain",
            default: true,
          }}
        >
          <Row gutter={[16, 8]} align="bottom">
            <Col xs={24} md={7}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Enter your name" }]}
              >
                <Input placeholder="Your name" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={5}>
              <Form.Item
                name="time_zone"
                label="Time zone"
                rules={[{ required: true, message: "Select a time zone" }]}
              >
                <Select options={TIMEZONE_OPTIONS} size="large" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={5}>
              <Form.Item
                name="avilibilityType"
                label="Type"
                rules={[{ required: true, message: "Select Virtual or In Person" }]}
              >
                <Select
                  placeholder="Select type"
                  options={typeOptions}
                  size="large"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item
                name="location"
                label="Location"
                rules={[{ required: true, message: "Select a location" }]}
              >
                {/* Always a real city, even for Virtual: the backend resolves the
                    appointment's timezone from this value at booking time
                    (ZoomServices.ConvertUtcToPlace), so anything other than a
                    known place name throws "Time zone for 'X' not found". */}
                <Select
                  placeholder={avilibilityType ? "Select a location" : "Select a type first"}
                  options={LOCATION_OPTIONS}
                  size="large"
                  style={{ width: "100%" }}
                  disabled={!avilibilityType}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={3}>
              <Form.Item
                name="default"
                label="Default"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <div style={{ height: 40, display: "flex", alignItems: "center" }}>
                  <Switch />
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "8px 0 16px" }} />

          <Card
            type="inner"
            title={
              <Space size={10}>
                <span>Add a segment</span>
                <Tag color="blue">Local time</Tag>
              </Space>
            }
            bordered
            bodyStyle={{ padding: 16 }}
          >
            <Row gutter={[12, 0]} align="bottom">
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="date"
                  label="Date"
                  rules={[{ required: true, message: "Pick a date" }]}
                >
                  <DatePicker style={{ width: "100%" }} size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6} md={6}>
                <Form.Item
                  name="startTime"
                  label="Start"
                  rules={[{ required: true, message: "Pick a start time" }]}
                >
                  <TimePicker
                    style={{ width: "100%" }}
                    format="h:mm A"
                    use12Hours
                    minuteStep={5}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6} md={6}>
                <Form.Item
                  name="endTime"
                  label="End"
                  rules={[{ required: true, message: "Pick an end time" }]}
                >
                  <TimePicker
                    style={{ width: "100%" }}
                    format="h:mm A"
                    use12Hours
                    minuteStep={5}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={4}>
                <Form.Item label=" " colon={false}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={onAddSegment} block size="large">
                    Add
                  </Button>
                </Form.Item>
              </Col>
            </Row>

            <div style={{ marginTop: 8 }}>
              {segments.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No segments yet"
                  style={{ margin: "10px 0 6px" }}
                />
              ) : (
                <List
                  size="small"
                  bordered
                  style={{ borderRadius: 12, overflow: "hidden" }}
                  dataSource={segments}
                  renderItem={(s) => (
                    <List.Item
                      key={`${s.start}|${s.end}`}
                      actions={[
                        <Popconfirm
                          key="remove"
                          title="Remove this segment?"
                          okText="Remove"
                          okButtonProps={{ danger: true }}
                          cancelText="Cancel"
                          onConfirm={() =>
                            setSegments((prev) =>
                              prev.filter((x) => !(x.start === s.start && x.end === s.end)),
                            )
                          }
                        >
                          <Button danger type="text" icon={<DeleteOutlined />}>
                            Remove
                          </Button>
                        </Popconfirm>,
                      ]}
                    >
                      <Space direction="vertical" size={0}>
                        <Text strong>
                          {formatIsoForDisplay(s.start)} → {formatIsoForDisplay(s.end)}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {s.start} → {s.end}
                        </Text>
                      </Space>
                    </List.Item>
                  )}
                />
              )}
            </div>
          </Card>

          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 16 }}>
            <Button onClick={() => setSegments([])} disabled={!segments.length}>
              Clear
            </Button>
            <Button type="primary" onClick={onSubmit} loading={submitting} disabled={!segments.length}>
              Save availability
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
}

